// --- Our Modules --------------------------------------------------------------------------------
import type { Lang } from "@/hooks/useLang/types";
import useTranslation from "@/hooks/useTranslation";
import { isRtlLang } from "@/utils";
import type { TyLangValues } from "@/utils";
import "./styles.css";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    label: string;
    /**
     * Passed in rather than read from `useStoreLanguages`, because `CreateStoreScreen` renders this
     * before the store exists — there is nothing to read the list from yet.
     */
    languages: Lang[];
    values: TyLangValues;
    onChange: (lang: Lang, value: string) => void;
    placeholders?: Partial<Record<Lang, string>>;
    disabled?: boolean;
    /** Renders a textarea instead of a single line, for text where the store's own line breaks matter. */
    multiline?: boolean;
    rows?: number;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * One text field per language the store actually offers. A store that only sells in Arabic never sees
 * an English box at all.
 *
 * Filling every field is not required — one is enough, and the API drops the blanks. That is left
 * unsaid in the UI on purpose: every read path falls back across languages, so a half-filled record is
 * a valid record, not a warning to raise.
 */
export default function MultiLangInput({ label, languages, values, onChange, placeholders, disabled, multiline, rows = 4 }: Props) {
    const t = useTranslation();

    // With one language there is nothing to tell apart, so the card's own label carries the meaning.
    const showTags = languages.length > 1;

    return (
        <div className="multi-lang-input">
            <div className="multi-lang-input-label">
                <img src="/icons/language-icon.svg" alt="" />
                <p>{label}</p>
            </div>
            <div className="multi-lang-input-row">
                {languages.map((lang) => (
                    <div key={lang} className="multi-lang-input-field-wrap">
                        {showTags && <span className="multi-lang-input-tag">{t.languages[lang]}</span>}
                        {multiline ? (
                            <textarea
                                rows={rows}
                                dir={isRtlLang(lang) ? "rtl" : "ltr"}
                                className="multi-lang-input-field multi-lang-input-field--multiline"
                                value={values[lang] ?? ""}
                                onChange={(e) => onChange(lang, e.target.value)}
                                placeholder={placeholders?.[lang]}
                                disabled={disabled}
                            />
                        ) : (
                            <input
                                type="text"
                                dir={isRtlLang(lang) ? "rtl" : "ltr"}
                                className="multi-lang-input-field"
                                value={values[lang] ?? ""}
                                onChange={(e) => onChange(lang, e.target.value)}
                                placeholder={placeholders?.[lang]}
                                disabled={disabled}
                            />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}
